import { CircularProgress, FormControlLabel, Switch } from "@mui/material";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useAppSelector } from "store/store";

import { transformSubjectsToCategories } from "../permissionsHelpers";
import PermissionCard from "../shared/PermissionCard";
import PermissionSection from "../shared/PermissionSection";
import PermissionSwitch from "../shared/PermissionSwitch";
import type { PermissionItem } from "../types";
import styles from "./style.module.scss";

interface PermissionState {
    [key: string]: boolean | any[];
}

interface PermissionsTabProps {
    permissions: PermissionState;
    onPermissionChange: (permissionKey: string, value: boolean | any[]) => void;
    readOnly?: boolean;
}

const PermissionsTab = ({ permissions, onPermissionChange, readOnly = false }: PermissionsTabProps) => {
    const { subjects, actions, subjectsFetchStatus, actionsFetchStatus } = useAppSelector((state) => state.permissions);
    const [t] = useTranslation();

    // Transform backend subjects into UI categories and filter out "manage" actions
    const permissionCategories = useMemo(() => {
        const categories = transformSubjectsToCategories(t, subjects, actions);

        // Filter out permissions with "manage" action
        return categories
            .map((category) => ({
                ...category,
                sections: category.sections.map((section) => ({
                    ...section,
                    permissions: section.permissions.filter((perm) => perm.action !== "manage"),
                })),
            }))
            .filter((category) => category.sections.some((section) => section.permissions.length > 0));
    }, [t, subjects, actions]);

    // Collect all permission IDs (excluding "manage" actions)
    const allPermissionIds = useMemo(() => {
        const ids: string[] = [];
        const collectIds = (item: PermissionItem) => {
            if (item.action !== "manage") {
                ids.push(item.id);
            }
            if (item.children) {
                item.children.forEach(collectIds);
            }
        };
        permissionCategories.forEach((category) => {
            category.sections.forEach((section) => {
                section.permissions.forEach(collectIds);
            });
        });
        return ids;
    }, [permissionCategories]);

    // Check if all permissions are selected
    const areAllSelected = useMemo(() => {
        return allPermissionIds.length > 0 && allPermissionIds.every((id) => permissions[id] === true);
    }, [allPermissionIds, permissions]);

    // Handle select/deselect all
    const handleSelectAll = (checked: boolean) => {
        allPermissionIds.forEach((id) => {
            onPermissionChange(id, checked);
        });
    };

    const renderPermissionItem = (permission: PermissionItem) => {
        const hasChildren = permission.children && permission.children.length > 0;
        const isParentChecked = permissions[permission.id] === true;

        return (
            <div key={permission.id}>
                <PermissionSwitch
                    id={permission.id}
                    label={permission.label}
                    checked={isParentChecked}
                    onChange={(checked) => onPermissionChange(permission.id, checked)}
                    nested={permission.nested}
                    disabled={readOnly}
                />

                {hasChildren && isParentChecked && (
                    <div className={styles["nested-permissions"]}>
                        {permission.children!.map((child) => renderPermissionItem({ ...child, nested: true }))}
                    </div>
                )}
            </div>
        );
    };

    return (
        <div className={styles["permissions-tab"]}>
            {subjectsFetchStatus === "pending" || actionsFetchStatus === "pending" ? (
                <div style={{ padding: "40px", textAlign: "center" }}>
                    <CircularProgress />
                </div>
            ) : permissionCategories.length === 0 ? (
                <div style={{ padding: "20px", textAlign: "center" }}>{t("Common.NoDataAvailable")}</div>
            ) : (
                <>
                    <div className={styles["select-all-container"]}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={areAllSelected}
                                    onChange={(e) => handleSelectAll(e.target.checked)}
                                    disabled={readOnly}
                                />
                            }
                            label={t("Roles.Modal.SelectAllPermissions")}
                        />
                    </div>
                    {permissionCategories.map((category) => (
                        <PermissionCard
                            key={category.id}
                            icon={category.icon}
                            title={category.title}
                            description={category.description}
                        >
                            {category.sections.map((section, sectionIndex) => {
                                // Only render PermissionSection if there's a title
                                const content = section.permissions.map(renderPermissionItem);

                                return section.title ? (
                                    <PermissionSection key={sectionIndex} title={section.title}>
                                        {content}
                                    </PermissionSection>
                                ) : (
                                    <div key={sectionIndex}>{content}</div>
                                );
                            })}
                        </PermissionCard>
                    ))}
                </>
            )}
        </div>
    );
};

export default PermissionsTab;
